import React from "react";
import {
  Description,
  ThumbUp,
  SupervisorAccount,
  Calculate,
  Storage, // Data Management
  Settings, // System Management
  PlayArrow,
  DeleteOutline,
  AccessTime,
  CheckCircle,
} from "@mui/icons-material";
import { Archive, InsertChart, History, AssignmentLate } from "@mui/icons-material";
import { People, Gavel, Shield, MonetizationOn, Category, VerifiedUser, AccountBalance, LocationOn, SupportAgent, Campaign, ContactSupport, Badge, Business } from "@mui/icons-material";
import Navbar from "../../../globals/components/Navbar";
import InternalUserDashboard from "../../../globals/components/InternalUserDashboard";
import UnpaidBondsCard from "../../../globals/components/UnpaidBondsCard";
import { Box, Paper, Typography, CircularProgress, Button, Chip, Tooltip, IconButton, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Alert, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { useSelector } from "react-redux";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { apiClient } from "../../../globals/utils/api";
import { toast } from "react-toastify";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// map compliance statuses to UI-friendly values
const toIssue = (fileName, payload) => {
  const issues = [];
  const sig = payload?.signature_validation;
  const pii = payload?.pii_screening;
  const mapStatus = (st) => (st === "PASS" ? "pass" : st === "NEEDS_FIX" ? "fail" : "unknown");
  if (sig) {
    issues.push({
      key: "signature",
      title: "Signature Validation",
      status: mapStatus(sig.status),
      summary: sig.status === "PASS" ? "No issues detected" : sig.status === "NEEDS_FIX" ? "Signature needs attention" : "Unable to determine",
      details: Array.isArray(sig.what_we_found) ? sig.what_we_found : [],
      nextSteps: Array.isArray(sig.how_to_fix) ? sig.how_to_fix : [],
    });
  }
  if (pii) {
    issues.push({
      key: "pii",
      title: "PII Screening",
      status: mapStatus(pii.status),
      summary: pii.status === "PASS" ? "No PII visible" : pii.status === "NEEDS_FIX" ? "Unredacted PII found" : "Unable to determine",
      details: Array.isArray(pii.what_we_found) ? pii.what_we_found : [],
      nextSteps: Array.isArray(pii.how_to_fix) ? pii.how_to_fix : [],
    });
  }
  if (!issues.length) {
    issues.push({
      key: "ok",
      title: "Checks",
      status: "unknown",
      summary: "No structured results returned",
      details: [],
      nextSteps: [],
    });
  }
  return {
    name: fileName,
    sizeKb: null,
    issues,
  };
};

const SimpleUserHome = () => {
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  const { user } = auth;
  const internalUserId = useSelector((state) => state.auth.role_id);
  const [userFeatures, setUserFeatures] = useState([]);
  const [cachedBonds, setCachedBonds] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const [totalCount, setTotalCount] = useState(0);
  const [hasMoreActivities, setHasMoreActivities] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [missingIndemnityBonds, setMissingIndemnityBonds] = useState([]);
  const [loadingIndemnity, setLoadingIndemnity] = useState(false);
  const [markingIndemnity, setMarkingIndemnity] = useState(null);
  const [unassignedBonds, setUnassignedBonds] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);

  // Dialog state for marking indemnity
  const [indemnityDialogOpen, setIndemnityDialogOpen] = useState(false);
  const [selectedBondForIndemnity, setSelectedBondForIndemnity] = useState(null);
  const [indemnityOption, setIndemnityOption] = useState(null); // 'offline' or 'upload'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationResults, setValidationResults] = useState([]);
  const hasAnyValidationErrors = useMemo(
    () => validationResults.some((f) => f.issues.some((i) => i.status === "fail")),
    [validationResults]
  );
  const hasBlockingErrors = useMemo(
    () => validationResults.some((f) => f.issues.some((i) => i.status === "fail" && i.key !== "pii")),
    [validationResults]
  );
  const hasPiiWarningsOnly = useMemo(
    () => hasAnyValidationErrors && !hasBlockingErrors,
    [hasAnyValidationErrors, hasBlockingErrors]
  );

  useEffect(() => {
    const loadDrafts = async () => {
      if (!user?.id) return;
      try {
        setLoadingDrafts(true);
        const rows = await apiClient(`/bond-cache-requester/${user.id}/`, "GET");
        setCachedBonds(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.error("Failed to load cached bonds", e);
        setCachedBonds([]);
      } finally {
        setLoadingDrafts(false);
      }
    };
    loadDrafts();
  }, [user?.id]);

  // Load missing indemnity bonds (no filter for internal user)
  const loadMissingIndemnity = useCallback(async () => {
    try {
      setLoadingIndemnity(true);
      const data = await apiClient("/missing-indemnity-bonds", "GET");
      setMissingIndemnityBonds(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load missing indemnity bonds", e);
      setMissingIndemnityBonds([]);
    } finally {
      setLoadingIndemnity(false);
    }
  }, []);

  useEffect(() => {
    loadMissingIndemnity();
  }, [location.pathname, loadMissingIndemnity]);

  const loadUnassignedBonds = useCallback(async () => {
    try {
      setLoadingUnassigned(true);
      const data = await apiClient("/unassigned-paralegal-bonds", "GET");
      setUnassignedBonds(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load unassigned bonds", e);
      setUnassignedBonds([]);
    } finally {
      setLoadingUnassigned(false);
    }
  }, []);

  useEffect(() => {
    loadUnassignedBonds();
  }, [location.pathname, loadUnassignedBonds]);

  const handleMarkIndemnityClick = (bond) => {
    setSelectedBondForIndemnity(bond);
    setIndemnityDialogOpen(true);
    setIndemnityOption(null);
    setSelectedFiles([]);
  };

  const handleMarkIndemnityOffline = async () => {
    if (!selectedBondForIndemnity) return;
    try {
      setMarkingIndemnity(selectedBondForIndemnity.bond_id);
      await apiClient("/set-indemnity-received/", "POST", {
        bond_id: selectedBondForIndemnity.bond_id,
        bond_type: selectedBondForIndemnity.bond_type.toLowerCase(),
      });
      toast.success("Indemnity marked as received successfully");
      setIndemnityDialogOpen(false);
      setSelectedBondForIndemnity(null);
      // Refetch to get the latest data
      await loadMissingIndemnity();
    } catch (e) {
      console.error("Failed to mark indemnity as received", e);
      toast.error("Failed to mark indemnity as received");
    } finally {
      setMarkingIndemnity(null);
    }
  };

  const handleFilesSelected = (event) => {
    const all = Array.from(event.target.files || []);
    const files = all.slice(0, 4); // API accepts up to 4
    const pdfsOnly = files.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (pdfsOnly.length !== files.length) {
      toast.error("Only PDF files are allowed.");
    }
    setSelectedFiles(pdfsOnly);
  };

  const handleClearFiles = () => {
    setSelectedFiles([]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleViewFile = (file) => {
    try {
      const url = URL.createObjectURL(file);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      console.error("Preview failed", e);
      toast.error("Unable to preview file.");
    }
  };

  const [processingOpen, setProcessingOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const handleSubmitIndemnityFiles = async () => {
    if (!selectedBondForIndemnity?.bond_id) {
      toast.error("Missing bond information.");
      return;
    }
    if (!selectedFiles.length) {
      toast.error("Please select at least one PDF to upload.");
      return;
    }
    setIsSubmitting(true);
    setProcessingOpen(true);
    try {
      const form = new FormData();
      selectedFiles.forEach((f) => form.append("files", f));
      form.append("use_hg_sales_key", "false");
      const res = await apiClient("/indemnity-compliance-check/", "POST", form, {}, {});
      // Normalize results
      const resultsArr = Array.isArray(res?.results) ? res.results : [];
      const mapped = resultsArr.map((r, idx) => {
        const name = r?.file_name || selectedFiles[idx]?.name || `file_${idx + 1}.pdf`;
        const base = toIssue(name, r);
        const sz = selectedFiles[idx]?.size ? Math.max(1, Math.round(selectedFiles[idx].size / 1024)) : null;
        return { ...base, sizeKb: sz };
      });
      setValidationResults(mapped);
      setValidationOpen(true);
    } catch (e) {
      console.error("Compliance check failed", e);
      toast.error(e?.error || "Failed to analyze indemnity PDF(s).");
    } finally {
      setIsSubmitting(false);
      setProcessingOpen(false);
    }
  };

  const handleFinalizeIndemnitySubmit = async () => {
    if (!selectedBondForIndemnity?.bond_id || !selectedFiles.length) return;
    setIsFinalizing(true);
    try {
      const form = new FormData();
      selectedFiles.slice(0, 4).forEach((f) => form.append("files", f));
      form.append("bond_id", String(selectedBondForIndemnity.bond_id));
      const lawfirmId = null; // Internal users don't have lawfirm_id
      const pgId = auth?.pg_id;
      if (lawfirmId) form.append("lawfirm_id", String(lawfirmId));
      if (pgId) form.append("professional_guardian_id", String(pgId));
      if (user?.id) form.append("requester_id", String(user.id));
      form.append("status", "pending");
      await apiClient("/manually-signed-indemnities/", "POST", form, {}, {});

      // Also mark indemnity as received
      await apiClient("/set-indemnity-received/", "POST", {
        bond_id: selectedBondForIndemnity.bond_id,
        bond_type: selectedBondForIndemnity.bond_type.toLowerCase(),
      });

      toast.success("Indemnity document(s) submitted successfully.");
      setSelectedFiles([]);
      setValidationOpen(false);
      setIndemnityDialogOpen(false);
      setSelectedBondForIndemnity(null);
      // Refetch to get the latest data
      await loadMissingIndemnity();
    } catch (e) {
      console.error("Submit failed", e);
      toast.error(e?.error || "Failed to submit indemnity files.");
    } finally {
      setIsFinalizing(false);
    }
  };

  const getBondUrl = (bondType, bondId) => {
    if (bondType === "VA") return `/vermost-va-bond-update/${bondId}`;
    if (bondType === "Trustee") return `/vermost-trustee-bond-update/${bondId}`;
    if (bondType === "Probate") return `/vermost-probate-bond-update/${bondId}`;
    if (bondType === "Blanket") return `/vermost-blanket-bond-update/${bondId}`;
    return `/vermost-guardianship-bond-update/${bondId}`;
  };

  useEffect(() => {
    const fetchInternalUser = async () => {
      try {
        if (!internalUserId) return;
        const response = await apiClient(`/get-internal-user/${internalUserId}`, "GET");
        setUserFeatures(Array.isArray(response?.features) ? response.features : []);
      } catch (error) {
        console.error("Error fetching internal user:", error);
        setUserFeatures([]);
      }
    };
    fetchInternalUser();
  }, [internalUserId]);

  useEffect(() => {
    const loadActivities = async (reset = true) => {
      try {
        setLoadingActivities(true);
        const targetPage = reset ? 1 : page;
        const data = await apiClient(`/bonds-feed/?page=${targetPage}&page_size=${pageSize}`, "GET");
        const results = Array.isArray(data?.results) ? data.results : [];
        if (reset) {
          setActivities(results);
          setPage(1);
        } else {
          setActivities((prev) => [...prev, ...results]);
        }
        const count = Number(data?.count || 0);
        setTotalCount(count);
        setHasMoreActivities(targetPage * pageSize < count);
      } catch (e) {
        console.error("Failed to load recent activity", e);
        if (reset) setActivities([]);
        setHasMoreActivities(false);
      } finally {
        setLoadingActivities(false);
      }
    };
    loadActivities(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMoreActivities = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    try {
      setLoadingActivities(true);
      const data = await apiClient(`/bonds-feed/?page=${nextPage}&page_size=${pageSize}`, "GET");
      const results = Array.isArray(data?.results) ? data.results : [];
      setActivities((prev) => [...prev, ...results]);
      const count = Number(data?.count || totalCount || 0);
      setTotalCount(count);
      setHasMoreActivities(nextPage * pageSize < count);
    } catch (e) {
      console.error("Failed to load more activity", e);
      setHasMoreActivities(false);
    } finally {
      setLoadingActivities(false);
    }
  };

  const MAX_DESC_LEN = 160;

  const getActivityText = (item) => {
    const status = String(item?.status || '').toLowerCase();
    const bondType = item?.bond_type || '—';
    const name = item?.name || '—';
    const requester = item?.requester_name || 'A user';
    if (status === 'active') {
      return `${requester} submitted a ${bondType} bond for ${name}, please review.`;
    }
    if (status === 'approved') {
      if (bondType.toLowerCase() === 'blanket') return `The Verification Certificate for ${requester} for ${name} was Created, please review.`;
      return `The ${bondType} bond requested by ${requester} for ${name} was Approved, please review.`;
    }
    if (status === 'cancelled' || status === 'canceled') {
      return `The ${bondType} bond requested by ${requester} for ${name} was Cancelled, please review.`;
    }
    if (status === 'endorsed') {
      const byName = item?.contact_name || requester;
      const desc = item?.endorsement_description ? `, ${item.endorsement_description}` : '';
      return `The ${bondType} bond for ${name} was endorsed by ${byName}${desc}, please review.`;
    }
    if (status === 'updated') {
      const field = item?.field_name;
      const oldV = item?.old_value ?? '—';
      const newV = item?.new_value ?? '—';
      const change = field ? `, ${field} changed from ${oldV} to ${newV}` : '';
      return `Data Correction was applied on ${bondType} bond for ${name} by ${requester}${change}, please review.`;
    }
    return `${requester} submitted a ${bondType} bond for ${name}, please review.`;
  };

  const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const getKeywordsForItem = (item) => {
    const status = String(item?.status || '').toLowerCase();
    if (status === 'active') return ['submitted'];
    if (status === 'approved') return String(item?.bond_type || '').toLowerCase() === 'blanket' ? ['Created'] : ['Approved'];
    if (status === 'cancelled' || status === 'canceled') return ['Cancelled'];
    if (status === 'endorsed') return ['endorsed'];
    if (status === 'updated') return ['Data Correction', 'changed'];
    return [];
  };
  const renderHighlighted = (text, item) => {
    const keywords = getKeywordsForItem(item);
    if (!keywords.length || !text) return text;
    const pattern = new RegExp(`(${keywords.map(escapeRegExp).join('|')})`, 'gi');
    const parts = String(text).split(pattern);
    const keywordSet = new Set(keywords.map((k) => k.toLowerCase()));
    return parts.map((part, i) =>
      keywordSet.has(String(part).toLowerCase()) ? <strong key={i}>{part}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
    );
  };

  const recentActiveCount = useMemo(() => {
    let count = 0;
    for (const it of activities) {
      if (String(it?.status || '').toLowerCase() === 'active') count++;
      else break;
    }
    return count;
  }, [activities]);

  const formatRelativeTime = (input) => {
    if (!input) return "";
    const date = new Date(input);
    if (Number.isNaN(date.getTime())) return "";
    const diffMs = Date.now() - date.getTime();
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 45) return "Just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    const years = Math.floor(months / 12);
    return `${years}y ago`;
  };

  const sortedCachedBonds = Array.isArray(cachedBonds)
    ? [...cachedBonds].sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0))
    : [];
  // Build feature-filtered options for Data Management
  const allDataMgmtOptions = [
    {
      name: "Bond Archives",
      path: "/vermost_bond_archives",
      feature: "bond_archives",
      icon: <Archive fontSize="large" />,
    },
    {
      name: "Analytics",
      path: "/analytics",
      feature: "analytics",
      icon: <InsertChart fontSize="large" />,
    },
    {
      name: "Create Historic Bond",
      path: "/bond-creation-outsider-historic",
      feature: "create_historic_bonds",
      icon: <History fontSize="large" />,
    },
  ];
  const dataManagementOptions = allDataMgmtOptions.filter((opt) =>
    userFeatures.includes(opt.feature)
  );

  // Build feature-filtered options for Account Management
  const allAccountMgmtOptions = [
    {
      name: "Internal Users",
      path: "/vermost_internal_users",
      feature: "internal_users",
      icon: <People fontSize="large" />,
    },
    {
      name: "Law Firms",
      path: "/vermost_lawfirm_list",
      feature: "law_firms",
      icon: <Gavel fontSize="large" />,
    },
    {
      name: "Professional Guardians",
      path: "/vermost-professional-guardian-list",
      feature: "professional_guardians",
      icon: <Shield fontSize="large" />,
    },
    {
      name: "Legal Service Paralegals",
      path: "/vermost_paralegals_list",
      feature: "paralegals",
      icon: <Badge fontSize="large" />,
    },
    {
      name: "Law Firm Users",
      path: "/vermost_law_firm_users",
      feature: "law_firm_users",
      icon: <Business fontSize="large" />,
    },
  ];
  const accountManagementOptions = allAccountMgmtOptions.filter((opt) =>
    userFeatures.includes(opt.feature)
  );

  // Build feature-filtered options for System Management
  const allSystemMgmtOptions = [
    {
      name: "Approve Bonds",
      path: "/vermost_approve_bonds",
      feature: "approve_bonds",
      icon: <ThumbUp fontSize="large" />,
    },
    {
      name: "Manage Premiums",
      path: "/vermost_premiums_manager",
      feature: "premiums",
      icon: <MonetizationOn fontSize="large" />,
    },
    {
      name: "Bond Types",
      path: "/bondtypes",
      feature: "bond_types",
      icon: <Category fontSize="large" />,
    },
    {
      name: "Bond Numbers",
      path: "/bond_numbers",
      feature: "bond_numbers",
      icon: <VerifiedUser fontSize="large" />,
    },
    {
      name: "Carriers",
      path: "/vermost_carrier_list",
      feature: "carriers",
      icon: <AccountBalance fontSize="large" />,
    },
    {
      name: "States",
      path: "/states",
      feature: "states",
      icon: <LocationOn fontSize="large" />,
    },
    {
      name: "All Enhancement Requests",
      path: "/view-enhancement-requests",
      feature: "enhancement_request",
      icon: <SupportAgent fontSize="large" />,
    },
    {
      name: "Support Requests",
      path: "/admin/support-requests",
      feature: "support_requests",
      icon: <ContactSupport fontSize="large" />,
    },
    {
      name: "Announcements",
      path: "/announcements",
      feature: "announcements",
      icon: <Campaign fontSize="large" />,
    },
  ];
  const systemManagementOptions = allSystemMgmtOptions.filter((opt) =>
    userFeatures.includes(opt.feature)
  );

  const links = [
    {
      action_value: 'Bond Operations',
      action_description: 'Covers end-to-end surety bond workflows such as creating and reviewing submissions.',
      values: [
        { name: 'Request a Bond', path: '/bond-creation-outsider', icon: <Description /> },
        { name: 'Approve Bonds', path: '/vermost_approve_bonds', icon: <ThumbUp /> },
      ],
    },
    {
      action_value: 'Management',
      action_description: 'Manage accounts, data, and system configuration in one place.',
      values: [
        { name: 'Account Management', icon: <SupervisorAccount />, options: accountManagementOptions },
        { name: 'Data Management', icon: <Storage />, options: dataManagementOptions },
        { name: 'System Management', icon: <Settings />, options: systemManagementOptions },
      ],
    },
    {
      action_value: 'Tools',
      action_description: 'Utilities and calculators to support your workflows.',
      values: [
        { name: 'Bond Calculator', path: '/premium_calculator', icon: <Calculate /> },
      ],
    },
  ];

  return (
    <>
      <Navbar />
      <InternalUserDashboard title="Vermost Internal User Dashboard" links={links} />
      <Box px={3}>
        <Box sx={{ display: { xs: 'block', md: 'grid' }, gridTemplateColumns: { md: '1fr 1fr' }, gap: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 1,
              background: (theme) => (theme.palette.mode === 'light' ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)' : theme.palette.background.paper),
              border: '1px solid rgba(37, 99, 235, 0.2)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              mb: 2
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.25, color: '#333333' }}>Recent Activity</Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>Your latest interactions and updates</Typography>
              </Box>
              {loadingActivities && <CircularProgress size={18} />}
            </Box>

            <Box>
              {!selectedActivity ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
                  {activities.length === 0 && !loadingActivities ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No recent active bonds.</Typography>
                  ) : (
                    activities.map((item, idx) => (
                      <Box key={`${item?.bond_id}-${idx}`} sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: (theme) => theme.palette.mode === 'light' ? '#ffffff' : 'background.default',
                        border: '1px solid rgba(37, 99, 235, 0.12)'
                      }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                              {(() => {
                                const text = getActivityText(item);
                                const tooLong = text.length > MAX_DESC_LEN;
                                const shown = tooLong ? (
                                  <>
                                    {renderHighlighted(text.slice(0, MAX_DESC_LEN), item)}…
                                  </>
                                ) : renderHighlighted(text, item);
                                return (
                                  <>
                                    {shown}
                                    {tooLong && (
                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => setSelectedActivity(item)}
                                        sx={{ ml: 0.5, p: 0, minWidth: 0, textTransform: 'none', color: '#2563eb' }}
                                      >
                                        See more
                                      </Button>
                                    )}
                                  </>
                                );
                              })()}
                            </Typography>
                            <Box mt={0.75} display="flex" alignItems="center" gap={1}>
                              {idx < recentActiveCount ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label="New"
                                  sx={{
                                    bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.18)',
                                    color: '#0f9f6e',
                                    borderColor: 'rgba(16,185,129,0.35)',
                                    fontWeight: 600,
                                    height: 22
                                  }}
                                />
                              ) : null}
                              {item?.lawfirm_name ? (
                                <Chip size="small" variant="outlined" label={item.lawfirm_name} />
                              ) : null}
                              {item?.event_at ? (
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                                  <AccessTime sx={{ fontSize: 14, color: 'text.disabled' }} />
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatRelativeTime(item.event_at)}</Typography>
                                </Box>
                              ) : null}
                            </Box>
                          </Box>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                              let url = '';
                              if (item?.bond_type === 'VA') url = `/vermost-va-bond-update/${item?.bond_id}`;
                              else if (item?.bond_type === 'Trustee') url = `/vermost-trustee-bond-update/${item?.bond_id}`;
                              else if (item?.bond_type === 'Probate') url = `/vermost-probate-bond-update/${item?.bond_id}`;
                              else if (item?.bond_type === 'Blanket') url = `/vermost-blanket-bond-update/${item?.bond_id}`;
                              else url = `/vermost-guardianship-bond-update/${item?.bond_id}`;
                              if (url) window.open(url, '_blank');
                            }}
                            sx={{ textTransform: 'none', color: '#2563eb', whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                          >
                            View Bond
                          </Button>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              ) : (
                <Box sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {renderHighlighted(getActivityText(selectedActivity), selectedActivity)}
                  </Typography>
                  <Box mt={0.75} display="flex" alignItems="center" gap={1}>
                    {(() => {
                      const i = activities.findIndex((x) => x === selectedActivity);
                      return i > -1 && i < recentActiveCount ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="New"
                          sx={{
                            bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.18)',
                            color: '#0f9f6e',
                            borderColor: 'rgba(16,185,129,0.35)',
                            fontWeight: 600,
                            height: 22
                          }}
                        />
                      ) : null;
                    })()}
                    {selectedActivity?.lawfirm_name ? (
                      <Chip size="small" variant="outlined" label={selectedActivity.lawfirm_name} />
                    ) : null}
                    {selectedActivity?.event_at ? (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                        <AccessTime sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatRelativeTime(selectedActivity.event_at)}</Typography>
                      </Box>
                    ) : null}
                  </Box>
                  <Box display="flex" justifyContent="space-between" mt={1.5}>
                    <Button size="small" variant="outlined" onClick={() => setSelectedActivity(null)} sx={{ textTransform: 'none', color: '#2563eb', borderColor: 'rgba(37, 99, 235, 0.3)' }}>Go back</Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        const item = selectedActivity;
                        let url = '';
                        if (item?.bond_type === 'VA') url = `/vermost-va-bond-update/${item?.bond_id}`;
                        else if (item?.bond_type === 'Trustee') url = `/vermost-trustee-bond-update/${item?.bond_id}`;
                        else if (item?.bond_type === 'Probate') url = `/vermost-probate-bond-update/${item?.bond_id}`;
                        else if (item?.bond_type === 'Blanket') url = `/vermost-blanket-bond-update/${item?.bond_id}`;
                        else url = `/vermost-guardianship-bond-update/${item?.bond_id}`;
                        if (url) window.open(url, '_blank');
                      }}
                      sx={{ textTransform: 'none', color: '#2563eb', whiteSpace: 'nowrap' }}
                    >
                      View Bond
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {hasMoreActivities && (
              <>
                <Divider sx={{ my: 1.5, borderColor: 'rgba(37, 99, 235, 0.12)' }} />
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={loadMoreActivities}
                    disabled={loadingActivities}
                    sx={{ textTransform: 'none', color: '#2563eb', borderColor: 'rgba(37, 99, 235, 0.3)' }}
                  >
                    See more
                  </Button>
                </Box>
              </>
            )}
          </Paper>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Unpaid Bonds Section (new) - visible to internal users across all law firms */}
            <UnpaidBondsCard />
            {/* Assignment Alerts Section */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1,
                background: (theme) => (theme.palette.mode === 'light' ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)' : theme.palette.background.paper),
                border: '1px solid rgba(37, 99, 235, 0.2)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                flex: '0 0 auto'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.25, color: '#333333' }}>Assignment Alerts</Typography>
                    <Chip size="small" color="error" label={unassignedBonds.length} />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>Bonds missing attorney assignment</Typography>
                </Box>
                {loadingUnassigned && <CircularProgress size={18} />}
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, minHeight: 0, maxHeight: 320 }}>
                {unassignedBonds.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>No bonds with missing assignments.</Typography>
                ) : (
                  unassignedBonds.map((bond) => (
                    <Box
                      key={bond.bond_id}
                      sx={{
                        p: 1,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: (theme) => theme.palette.mode === 'light' ? '#ffffff' : 'background.default',
                        border: '1px solid rgba(37, 99, 235, 0.12)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', noWrap: true, mb: 1 }}>
                          {bond.bond_number ? `${bond.full_name || '\u2014'} - ${bond.bond_number}` : (bond.full_name || '\u2014')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {bond.bond_type && `${bond.bond_type}`}
                          {bond.bond_type && bond.law_firm_name && ` \u2022 `}
                          {bond.law_firm_name && `${bond.law_firm_name}`}
                          {bond.status && ` \u2022 ${bond.status}`}
                          {bond.created_at && ` \u2022 ${formatRelativeTime(bond.created_at)}`}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const url = getBondUrl(bond.bond_type, bond.bond_id);
                          if (url) window.open(url, '_blank');
                        }}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.5 }}
                      >
                        View
                      </Button>
                    </Box>
                  ))
                )}
              </Box>
            </Paper>

            {/* Pending Signatures Section */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1,
                background: (theme) => (theme.palette.mode === 'light' ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)' : theme.palette.background.paper),
                border: '1px solid rgba(37, 99, 235, 0.2)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                flex: '0 0 auto'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.25, color: '#333333' }}>Pending Signatures</Typography>
                    <Chip size="small" color="warning" label={missingIndemnityBonds.length} />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>Bonds with pending signed indemnity agreements</Typography>
                </Box>
                {loadingIndemnity && <CircularProgress size={18} />}
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, minHeight: 0, maxHeight: 320 }}>
                {missingIndemnityBonds.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>No bonds with Pending Signatures.</Typography>
                ) : (
                  missingIndemnityBonds.map((bond) => (
                    <Box
                      key={bond.bond_id}
                      sx={{
                        p: 1,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: (theme) => theme.palette.mode === 'light' ? '#ffffff' : 'background.default',
                        border: '1px solid rgba(37, 99, 235, 0.12)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', noWrap: true, mb: 2 }}>
                          {bond.bond_number ? `${bond.full_name || '—'} - ${bond.bond_number}` : (bond.full_name || '—')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {bond.bond_type} • {bond.law_firm_name || 'No law firm'}
                          {bond.attorney_name && ` • ${bond.attorney_name}`}
                          {bond.created_at && ` • ${formatRelativeTime(bond.created_at)}`}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={0.5}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const url = getBondUrl(bond.bond_type, bond.bond_id);
                            if (url) window.open(url, '_blank');
                          }}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.5 }}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={markingIndemnity === bond.bond_id ? <CircularProgress size={14} /> : <CheckCircle sx={{ fontSize: 14 }} />}
                          onClick={() => handleMarkIndemnityClick(bond)}
                          disabled={markingIndemnity === bond.bond_id}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.5 }}
                        >
                          Mark as completed
                        </Button>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Paper>

            {/* Draft Bond Requests Section */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1,
                background: (theme) => (theme.palette.mode === 'light' ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)' : theme.palette.background.paper),
                border: '1px solid rgba(37, 99, 235, 0.2)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                flex: '0.85 1 auto',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.25, color: '#333333' }}>Draft Bond Requests</Typography>
                    <Chip size="small" color="primary" label={cachedBonds.length} />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>Continue working on your saved drafts.</Typography>
                </Box>
                {loadingDrafts && <CircularProgress size={18} />}
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
                {sortedCachedBonds.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    You have no saved bond requests.
                  </Typography>
                ) : (
                  sortedCachedBonds.slice(0, 5).map((row) => (
                    <Box
                      key={row?.bond_cache_id}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: (theme) => theme.palette.mode === 'light' ? '#ffffff' : 'background.default',
                        border: '1px solid rgba(37, 99, 235, 0.12)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', noWrap: true, mb: 1 }}>
                          {row?.name || 'Untitled'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {row?.bond_type && `${row.bond_type}`}
                          {row?.bond_type && row?.created_at && ` • `}
                          {row?.created_at && formatRelativeTime(row.created_at)}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={0.5}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PlayArrow sx={{ fontSize: 14 }} />}
                          onClick={() => window.location.assign((() => {
                            switch (row?.bond_type) {
                              case 'Probate': return `/vermost-probate-bond-request-cache/${row?.bond_cache_id}`;
                              case 'Guardianship': return `/vermost-guardianship-bond-request-cache/${row?.bond_cache_id}`;
                              case 'Trustee': return `/vermost-trustee-bond-request-cache/${row?.bond_cache_id}`;
                              case 'VA': return `/vermost-va-bond-request-cache/${row?.bond_cache_id}`;
                              case 'Blanket': return `/vermost-blanket-bond-request-cache/${row?.bond_cache_id}`;
                              default: return null;
                            }
                          })())}
                          disabled={!row?.bond_type}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.5, minWidth: 'auto', color: '#2563eb', borderColor: 'rgba(37, 99, 235, 0.3)' }}
                        >
                          Resume
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={deletingId === row?.bond_cache_id ? <CircularProgress size={14} /> : <DeleteOutline sx={{ fontSize: 14 }} />}
                          onClick={async () => {
                            if (!row?.bond_cache_id) return;
                            const confirmed = window.confirm('Delete this draft? This cannot be undone.');
                            if (!confirmed) return;
                            try {
                              setDeletingId(row?.bond_cache_id);
                              await apiClient(`/bond-cache/${row?.bond_cache_id}/`, 'DELETE');
                              setCachedBonds(prev => prev.filter(r => r?.bond_cache_id !== row?.bond_cache_id));
                            } catch (e) {
                              console.error('Failed to delete cached bond', e);
                            } finally {
                              setDeletingId(null);
                            }
                          }}
                          disabled={deletingId === row?.bond_cache_id}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.5, minWidth: 'auto', color: '#dc2626', borderColor: 'rgba(220, 38, 38, 0.3)' }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Indemnity Marking Dialog */}
      <Dialog
        open={indemnityDialogOpen}
        onClose={() => {
          setIndemnityDialogOpen(false);
          setSelectedBondForIndemnity(null);
          setIndemnityOption(null);
          setSelectedFiles([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Mark Indemnity as Completed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            How would you like to mark this indemnity as completed?
          </Typography>

          {!indemnityOption ? (
            <Stack spacing={2}>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={() => setIndemnityOption('offline')}
                sx={{ textTransform: 'none', py: 2 }}
              >
                <Box sx={{ textAlign: 'left', width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Submitted Offline
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    The indemnity was submitted through other means (mail, fax, etc.)
                  </Typography>
                </Box>
              </Button>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={() => setIndemnityOption('upload')}
                sx={{ textTransform: 'none', py: 2 }}
              >
                <Box sx={{ textAlign: 'left', width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Submit Indemnity Document(s)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Upload PDF document(s) for validation and submission
                  </Typography>
                </Box>
              </Button>
            </Stack>
          ) : indemnityOption === 'offline' ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Confirm that the indemnity documents have been submitted through other means (physical mail, email, fax, etc.) and will be processed accordingly.
              </Alert>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="text"
                  onClick={() => setIndemnityOption(null)}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleMarkIndemnityOffline}
                  disabled={markingIndemnity === selectedBondForIndemnity?.bond_id}
                >
                  {markingIndemnity === selectedBondForIndemnity?.bond_id ? 'Processing...' : 'Confirm'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Upload Indemnity PDFs
              </Typography>
              {(() => {
                const isProbateUnder50k = selectedBondForIndemnity?.bond_type === "Probate" && (parseFloat(selectedBondForIndemnity?.bond_amount) || 0) <= 50000;
                return (
                  <>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        disabled={isProbateUnder50k}
                        sx={{ textTransform: "none", color: "#2563eb", borderColor: "rgba(37, 99, 235, 0.3)" }}
                      >
                        Choose File(s)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          multiple
                          onChange={handleFilesSelected}
                        />
                      </Button>
                      {selectedFiles.length > 0 && (
                        <Button
                          variant="text"
                          onClick={handleClearFiles}
                          sx={{ textTransform: "none", color: "text.secondary" }}
                        >
                          Clear selection
                        </Button>
                      )}
                    </Box>
                    {isProbateUnder50k && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        This Indemnity Agreement does not need to be signed or submitted to Probate Bond Pros.
                      </Typography>
                    )}
                    {selectedFiles.length > 0 && (
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {selectedFiles.length} file(s) selected
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {selectedFiles.map((f, i) => {
                      const sizeKb = Math.max(1, Math.round((f.size || 0) / 1024));
                      return (
                        <Paper
                          key={`${f.name}-${i}`}
                          elevation={0}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            border: "1px solid rgba(37, 99, 235, 0.18)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PictureAsPdfIcon sx={{ color: "error.main" }} />
                            <Box>
                              <Typography variant="body2" sx={{ color: "text.primary" }}>
                                {f.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {sizeKb} KB
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Tooltip title="View">
                              <IconButton size="small" onClick={() => handleViewFile(f)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton size="small" color="error" onClick={() => handleRemoveFile(i)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                </Box>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                <Button
                  variant="text"
                  onClick={() => {
                    setIndemnityOption(null);
                    setSelectedFiles([]);
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitIndemnityFiles}
                  disabled={!selectedFiles.length || isSubmitting || isProbateUnder50k}
                >
                  {isSubmitting ? "Validating..." : "Validate & Submit"}
                </Button>
              </Box>
                  </>
                );
              })()}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Processing Popup */}
      <Dialog open={processingOpen} PaperProps={{ sx: { textAlign: "center", p: 3 } }}>
        <DialogContent>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Processing Your Submission
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Thank you for submitting the Indemnity Agreement. Please wait a
            moment as the Indemnity Agreement is being screened for submission.
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Validation Results Dialog */}
      <Dialog
        open={validationOpen}
        onClose={() => setValidationOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {hasBlockingErrors ? (
            <ErrorOutlineIcon color="error" />
          ) : hasPiiWarningsOnly ? (
            <WarningAmberIcon sx={{ color: "warning.main" }} />
          ) : (
            <CheckCircleIcon color="success" />
          )}
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Indemnity Validation Report
          </Typography>
          <Chip size="small" label="Probate Bond Pros AI" sx={{ ml: 1 }} />
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          {hasBlockingErrors ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              The validation agent found issues that need attention before submitting the file to Probate Bond Pros. Close this report and re-upload corrected PDFs.
            </Alert>
          ) : hasPiiWarningsOnly ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              PII exposure might be possible—please double-check before submitting. You may proceed if appropriate.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              All checks passed. You can safely proceed to submit these document(s).
            </Alert>
          )}
          <Stack spacing={1.25}>
            {validationResults.map((file, idx) => {
              const hasBlocking = file.issues.some((i) => i.status === "fail" && i.key !== "pii");
              const hasPiiOnly = file.issues.some((i) => i.status === "fail" && i.key === "pii");
              const borderColor = hasBlocking
                ? "1px solid rgba(220,38,38,0.35)"
                : hasPiiOnly
                  ? "1px solid rgba(237,108,2,0.35)"
                  : "1px solid rgba(16,185,129,0.35)";
              const iconColor = hasBlocking ? "error.main" : hasPiiOnly ? "warning.main" : "success.main";
              return (
                <Paper
                  key={`${file.name}-${idx}`}
                  elevation={0}
                  sx={{
                    p: 1.25,
                    borderRadius: 1,
                    border: borderColor,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PictureAsPdfIcon sx={{ color: iconColor }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {file.sizeKb} KB
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {file.issues.map((iss, i) => {
                        const isPiiWarning = iss.key === "pii" && iss.status === "fail";
                        const chipColor = iss.status === "fail" ? (isPiiWarning ? "warning" : "error") : "success";
                        const chipLabel = iss.status === "fail"
                          ? (isPiiWarning ? `${iss.title}: Warning` : `${iss.title}: Needs fix`)
                          : `${iss.title}: OK`;
                        return (
                          <Chip
                            key={`${iss.key}-${i}`}
                            size="small"
                            color={chipColor}
                            variant="outlined"
                            label={chipLabel}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                  {file.issues.map((iss, i) => {
                    const isPiiWarning = iss.key === "pii" && iss.status === "fail";
                    const issueColor = iss.status === "fail" ? (isPiiWarning ? "warning" : "error") : "success";
                    return (
                      <Accordion key={`${iss.key}-acc-${i}`} elevation={0} disableGutters sx={{ borderRadius: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {iss.status === "fail" ? (
                              isPiiWarning ? (
                                <WarningAmberIcon color="warning" fontSize="small" />
                              ) : (
                                <ErrorOutlineIcon color="error" fontSize="small" />
                              )
                            ) : (
                              <CheckCircleIcon color="success" fontSize="small" />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {iss.title}
                            </Typography>
                            <Chip
                              size="small"
                              color={issueColor}
                              label={iss.summary}
                              sx={{ ml: 0.5 }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {iss.status === "fail" ? (
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  What we found
                                </Typography>
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  {iss.details.map((d, di) => (
                                    <Typography key={di} variant="body2">• {d}</Typography>
                                  ))}
                                </Stack>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  How to fix
                                </Typography>
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  {iss.nextSteps.map((n, ni) => (
                                    <Typography key={ni} variant="body2">• {n}</Typography>
                                  ))}
                                </Stack>
                              </Box>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                What we found
                              </Typography>
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {iss.details.map((d, di) => (
                                  <Typography key={di} variant="body2">• {d}</Typography>
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          {hasBlockingErrors ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                Close this report to address issues and upload corrected files.
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="text" onClick={() => setValidationOpen(false)}>
                  Close
                </Button>
              </Box>
            </>
          ) : hasPiiWarningsOnly ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                Double-check for PII exposure; you may proceed if appropriate.
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="text" onClick={() => setValidationOpen(false)}>
                  Close
                </Button>
                <Button variant="contained" color="warning" onClick={handleFinalizeIndemnitySubmit} disabled={isFinalizing}>
                  {isFinalizing ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Submitting...</> : "Proceed"}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                Documents are ready to submit.
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="text" onClick={() => setValidationOpen(false)}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleFinalizeIndemnitySubmit} disabled={isFinalizing}>
                  {isFinalizing ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Submitting...</> : "Submit"}
                </Button>
              </Box>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SimpleUserHome;
